import useApi from "@/hooks/useApi";
import * as React from "react";
import { useEffect, useMemo } from "react";
import { Trigger, WorkflowListItem, triggerDefs } from "./types";
import IllustrationMyWorkflows from "@/icons/illustrations/IllustrationMyWorkflows";
import { Checkbox, WorkflowItem } from "./WorkflowHelpers";
import useWorkflows from "@/hooks/useWorkflows";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import useActivityLog from "@/hooks/useActivityLog";
import { useNavigate } from "react-router-dom";

export default function Workflows() {
  const { workflows, setWorkflows, fetch } = useWorkflows();
  const api = useApi();
  const { track } = useActivityLog();
  const { displayToast } = useToastAlert();
  const navigate = useNavigate();

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Split templates from regular workflows
  const templates = useMemo(
    () => (Array.isArray(workflows) ? workflows.filter((w) => w.isTemplate) : []),
    [workflows]
  );
  const regularWorkflows = useMemo(
    () => (Array.isArray(workflows) ? workflows.filter((w) => !w.isTemplate) : []),
    [workflows]
  );

  const groupedWorkflows = useMemo(() => {
    const result: Record<string, WorkflowListItem[]> = {};
    regularWorkflows.forEach((workflow) => {
      if (!result[workflow.triggerType]) {
        result[workflow.triggerType] = [];
      }
      result[workflow.triggerType].push(workflow);
    });
    return result;
  }, [regularWorkflows]);

  const triggers = useMemo(
    () => Object.keys(groupedWorkflows) as Trigger[],
    [groupedWorkflows]
  );

  if (workflows.length === 0) {
    return (
      <div className="p-16 bg-white rounded-2xl border border-slate-200 flex-col justify-center items-center gap-4 inline-flex">
        <div className="flex-col justify-center items-center gap-6 flex">
          <IllustrationMyWorkflows />
          <div className="flex-col justify-center items-center gap-2 flex">
            <div className="text-slate-800 text-lg">Workflows</div>
            <div className="text-center text-slate-500 text-sm">
              Your workflow list is currently empty.
              <br />
              Build your first workflow to enhance customer experiences and
              automate your processes.
            </div>
          </div>
        </div>
      </div>
    );
  }

  async function toggleWorkflow(id: number) {
    if (!workflows) return;

    await api.messaging.post(`Workflows/toggle/${id}`);

    const index = workflows.findIndex((w) => w.id === id);
    const workflow = workflows[index];
    const clone = [...workflows];
    clone[index] = { ...workflow, enabled: !workflow.enabled };
    setWorkflows(clone);

    track({
      category: "Workflows",
      action: workflow.enabled ? "WorkflowDisabled" : "WorkflowEnabled",
      entityType: "Workflow",
      entityId: String(id),
      entityName: workflow.name
    });
  }

  async function removeWorkflow(id: number) {
    if (workflows === null) return;

    const workflow = workflows.find((w) => w.id === id);
    try {
      const resp = await api.messaging.delete(`Workflows/${id}`);
      if (resp.status === 409) {
        const body = await resp.json().catch(() => ({}));
        displayToast(Severity.Error, body?.error ?? "Cannot delete this template. Detach linked workflows first.");
        return;
      }
    } catch {
      displayToast(Severity.Error, "Failed to delete workflow");
      return;
    }

    const clone = workflows?.filter((w) => w.id !== id);
    setWorkflows(clone);

    track({
      category: "Workflows",
      action: "WorkflowDeleted",
      entityType: "Workflow",
      entityId: String(id),
      entityName: workflow?.name
    });
  }

  async function detachWorkflow(id: number) {
    try {
      await api.messaging.post(`Workflows/detach/${id}`);
      const clone = workflows.map((w) =>
        w.id === id ? { ...w, templateId: null, templateName: null } : w
      );
      setWorkflows(clone);
      displayToast(Severity.Success, "Workflow detached. You can now edit it independently.");

      track({
        category: "Workflows",
        action: "WorkflowDetachedFromTemplate",
        entityType: "Workflow",
        entityId: String(id)
      });
    } catch {
      displayToast(Severity.Error, "Failed to detach workflow from template");
    }
  }

  async function duplicateWorkflow(id: number) {
    try {
      const workflowResp = await api.messaging.get(`Workflows/${id}`);
      const payload = await workflowResp.json();

      const createdDuplicatedWFResp = await api.messaging.post("Workflows", {
        trigger: payload.triggerType
      });

      let createdBody: any = null;
      try {
        createdBody = await createdDuplicatedWFResp.json();
      } catch (err) {
        // ignore json parse errors
      }

      let createdId: number | undefined;
      if (typeof createdBody === "number") {
        createdId = createdBody;
      } else if (createdBody && typeof createdBody === "object") {
        createdId =
          (createdBody.id as number) ??
          (createdBody.data && (createdBody.data.id as number)) ??
          (createdBody.workflowId as number) ??
          undefined;
      }

      if (
        !createdId &&
        createdDuplicatedWFResp &&
        (createdDuplicatedWFResp as any).headers
      ) {
        try {
          const location = (createdDuplicatedWFResp as any).headers.get
            ? (createdDuplicatedWFResp as any).headers.get("location") ||
              (createdDuplicatedWFResp as any).headers.get("Location")
            : null;
          if (location) {
            const m = location.match(/\/(\d+)(?:\D*$)/);
            if (m) createdId = parseInt(m[1], 10);
          }
        } catch (_e) {
          // ignore
        }
      }

      if (!createdId) {
        await fetch();
        return;
      }

      await api.messaging.put("Workflows", {
        id: createdId,
        name: `${payload.name} - Copy`,
        nodes: payload.nodes,
        triggerType: payload.triggerType
      });

      await fetch();
      displayToast(Severity.Success, `Workflow duplicated: ${payload.name}`);
    } catch (e) {
      console.error("Failed to duplicate workflow", e);
      displayToast(Severity.Error, "Failed to duplicate workflow");
    }
  }

  function renderTemplatesSection() {
    if (templates.length === 0) return null;

    return (
      <div className="p-4">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex gap-2 items-center font-bold text-violet-700">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
            Workflow Templates
          </div>
          <span className="text-xs text-slate-400">
            Changes to a template propagate to all linked workflows automatically
          </span>
        </div>
        <div className="bg-violet-50 border rounded-lg border-violet-200">
          <table cellPadding={10} className="w-full text-left">
            <thead>
              <tr className="border-b border-violet-200 h-12">
                <th className="w-1/12">
                  <div className="flex items-center">
                    <Checkbox />
                  </div>
                </th>
                <th className="w-1/4">Name</th>
                <th className="w-1/4">Created by</th>
                <th className="w-1/6">Updated</th>
                <th className="w-1/6">Status</th>
                <th className="w-1/12">Action</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template, i) => {
                const linked = regularWorkflows.filter((w) => w.templateId === template.id).length;
                return (
                  <WorkflowItem
                    key={template.id}
                    item={template}
                    isLast={i === templates.length - 1}
                    toggle={() => toggleWorkflow(template.id)}
                    remove={() => removeWorkflow(template.id)}
                    duplicate={() => duplicateWorkflow(template.id)}
                    linkedCount={linked}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderTrigger(trigger: Trigger, index: number) {
    return (
      <div className="p-4" key={index}>
        <div className="mb-4 flex">
          <div className="flex gap-2 items-center font-bold">
            {triggerDefs[trigger]}
          </div>
        </div>
        <div className="bg-white border rounded-lg border-slate-200">
          <table cellPadding={10} className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 h-12">
                <th className="w-1/12">
                  <div className="flex items-center">
                    <Checkbox />
                  </div>
                </th>
                <th className="w-1/4">Name</th>
                <th className="w-1/4">Created by</th>
                <th className="w-1/6">Updated</th>
                <th className="w-1/6">Status</th>
                <th className="w-1/12">Action</th>
              </tr>
            </thead>
            <tbody>
              {groupedWorkflows[trigger].map((workflow, i) => (
                <WorkflowItem
                  key={workflow.id}
                  item={workflow}
                  isLast={i === groupedWorkflows[trigger].length - 1}
                  toggle={() => toggleWorkflow(workflow.id)}
                  remove={() => removeWorkflow(workflow.id)}
                  duplicate={() => duplicateWorkflow(workflow.id)}
                  onDetach={workflow.templateId ? () => detachWorkflow(workflow.id) : undefined}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <>
      {renderTemplatesSection()}
      {triggers.map(renderTrigger)}
    </>
  );
}
